// UserRoleAssignDialog.js - Dialog for assigning roles to users (superadmin only)
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  DataGrid,
  authFetch,
  authFetchRequest
} from './SettingsCommon';

const UserRoleAssignDialog = ({ open, onClose, roles, onSave }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authFetch('/users/');
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
      setSelectedUser(null);
      setSelectedRoleId('');
    }
  }, [open]);

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRoleId) return;

    setSaving(true);
    setError(null);

    try {
      await authFetchRequest('/user-roles/assign', {
        user_id: selectedUser.id,
        role_id: parseInt(selectedRoleId)
      }, 'POST');

      // Refresh users list
      await fetchUsers();

      // Reset selection
      setSelectedUser(null);
      setSelectedRoleId('');

      // Notify parent
      onSave?.();
    } catch (err) {
      console.error('Error assigning role:', err);
      setError(`Failed to assign role: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.label || 'Unknown';
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name || 'unknown';
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'fkUserRole',
      headerName: 'Current Role',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          size="small"
          color={getRoleName(params.value) === 'superadmin' ? 'error' : 'primary'}
          variant="outlined"
        />
      )
    },
  ];

  const activeRoles = roles.filter(r => r.active);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Roles to Users</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select a user and assign a role. Only superadmin can perform this action.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 300, mb: 3 }}>
            <DataGrid
              rows={users}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5]}
              onRowClick={(params) => {
                setSelectedUser(params.row);
                setSelectedRoleId(params.row.fkUserRole || '');
              }}
              sx={{
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' }
              }}
            />
          </Box>
        )}

        {selectedUser && (
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Change role for: <strong>{selectedUser.username}</strong> ({selectedUser.email})
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>New Role</InputLabel>
                <Select
                  value={selectedRoleId}
                  label="New Role"
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                >
                  {activeRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleAssignRole}
                disabled={!selectedRoleId || selectedRoleId === selectedUser.fkUserRole || saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                Assign Role
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRoleAssignDialog;
